// /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/components/animations/AnimatedBlobs.tsx

import React, { useMemo, useState, useEffect } from "react";

export type BlobPalette = "karim" | "dark" | "mono" | "ocean" | "sunset";
export type BlobDensity = "low" | "medium" | "high";
export type BlobSpeed = "slow" | "normal" | "fast";
// NEU: Den 'variant'-Typ extrahiert, um ihn wiederverwenden zu können
export type BlobVariant = "hero" | "section" | "background";

interface AnimatedBlobsProps {
  className?: string;
  variant?: BlobVariant; // GEÄNDERT: Verwendet den extrahierten Typ
  palette?: BlobPalette;
  density?: BlobDensity;
  speed?: BlobSpeed;
  animated?: boolean;
  parallax?: boolean; // vergrößert Auslenkung
  blendMode?: "normal" | "screen" | "overlay" | "soft-light" | "plus-lighter" | "multiply";
  goo?: boolean; // Metaball-Effekt an/aus
}

interface BlobSpec {
  id: number;
  cx: number; // in %
  cy: number; // in %
  r: number;  // px
  delay: number; // s
  duration: number; // s
  kf: string; // keyframes name
}

// --- KORREKTUREN (Indexing-Fehler) ---

// Wir definieren die Maps außerhalb der Komponente.
// Das ist performanter und löst die TypeScript-Fehler.

// Löst den Fehler bei: { low: 3, ... }[effectiveDensity]
const DENSITY_MAP: Record<BlobDensity, number> = {
  low: 3,
  medium: 5,
  high: 7,
};

// Löst den Fehler bei: { slow: 1.6, ... }[speed]
const SPEED_MAP: Record<BlobSpeed, number> = {
  slow: 1.6,
  normal: 1.0,
  fast: 0.5,
};

// Löst den Fehler bei: ${v[variant]}
const VARIANT_MAP: Record<BlobVariant, string> = {
  hero: "z-0",
  section: "z-0 opacity-80",
  background: "z-0 opacity-40",
};
// --- ENDE KORREKTUREN ---


// GEÄNDERT: React.FC entfernt (Best Practice) und Props direkt typisiert
const AnimatedBlobs = ({
  className = "",
  variant = "hero",
  palette = "karim",
  density = "medium",
  speed = "fast",
  animated = true,
  parallax = false,
  blendMode = "screen",
  goo = false,
}: AnimatedBlobsProps) => { // <- Typisierung hier
  
  // --- START: Mobile Density Adjustment ---
  const MOBILE_BREAKPOINT = 768;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkDeviceSize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    checkDeviceSize();
    window.addEventListener("resize", checkDeviceSize);
    return () => window.removeEventListener("resize", checkDeviceSize);
  }, []);

  const effectiveDensity = useMemo(() => {
    if (!isMobile) {
      return density;
    }
    if (density === "high") return "medium";
    return "low";
  }, [isMobile, density]);
  // --- END: Mobile Density Adjustment ---

  // Farbpaletten
  const colorPalettes: Record<BlobPalette, string[]> = useMemo(
    // Dieser Record<...> (Fehler 2) war wahrscheinlich ein Folgefehler
    // und sollte jetzt auch behoben sein.
    () => ({
      karim: ["#00f5d4", "#9b5de5", "#f15bb5", "#fee440", "#00bbf9"],
      dark: ["#1a1a2e", "#16213e", "#0f3460", "#533483", "#2c2c54"],
      mono: ["#ffffff", "#e5e7eb", "#f3f4f6", "#d1d5db", "#f9fafb"],
      ocean: ["#a5f3fc", "#93c5fd", "#60a5fa", "#7dd3fc", "#38bdf8"],
      sunset: ["#ffb3c1", "#ffcf9f", "#ffd6a5", "#fdffb6", "#caffbf"],
    }),
    []
  );

  const colors = useMemo(() => colorPalettes[palette], [palette, colorPalettes]);

  // Anzahl Blobs
  const count = useMemo(
    // GEÄNDERT: Greift auf die typsichere Map zu
    () => DENSITY_MAP[effectiveDensity],
    [effectiveDensity]
  );

  // Geschwindigkeitsfaktor
  const speedFactor = useMemo(
    // GEÄNDERT: Greift auf die typsichere Map zu
    () => SPEED_MAP[speed],
    [speed]
  );

  // ... (Rest der seeded, blobs, keyframesCss, gradientCss Hooks bleibt gleich) ...
  // (Der Code von Zeile 100 bis 188 bleibt unverändert)

  // einfacher deterministischer Seed
  const seeded = (s: string) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
      h = (h << 5) - h + s.charCodeAt(i);
      h |= 0;
    }
    return Math.abs(h);
  };

  // Blobs mit unterschiedlichen Größen und sanfter Verteilung
  const blobs: BlobSpec[] = useMemo(() => {
    const arr: BlobSpec[] = [];
    for (let i = 0; i < count; i++) {
      const sv = seeded(`blob-${i}-${variant}-${palette}`);
      const base = 90 + (i % 4) * 40;
      const jitter = (sv % 40) - 20;
      const r = Math.max(80, Math.min(260, base + jitter));
      const spreadX = 60;
      const startX = 50;
      const step = count > 1 ? spreadX / (count - 1) : 0;
      const cx = startX + step * i + ((sv % 9) - 4) * 0.4;
      const cy = 50 + ((sv % 21) - 10) * 0.6;
      const baseDur = 36 + (sv % 24);
      const duration = Math.round(baseDur * speedFactor);
      const delay = (i * 1.35 + (sv % 700) / 1000) * speedFactor;
      arr.push({
        id: i, cx, cy, r, delay, duration, kf: `blobFloat-${variant}-${i}`,
      });
    }
    return arr;
  }, [count, variant, palette, speedFactor]);

  // Keyframes je Blob mit sanfter Drift in X/Y, optional höherer Amplitude durch parallax
  const keyframesCss = useMemo(() => {
    if (!blobs) {
      return "";
    }
    return blobs
      .map((b, i) => {
        const ax = (parallax ? 1.6 : 1.0) * (18 + (i % 5) * 6); // 18–42px
        const ay = (parallax ? 1.6 : 1.0) * (14 + ((i + 2) % 5) * 5); // 14–34px
        return `
          @keyframes ${b.kf} {
            0%   { transform: translate3d(0, 0, 0) scale(1); }
            20%  { transform: translate3d(${( ax * 0.6).toFixed(1)}px, ${(-ay * 0.5).toFixed(1)}px, 0) scale(1.03); }
            40%  { transform: translate3d(${(-ax * 0.9).toFixed(1)}px, ${(-ay * 0.2).toFixed(1)}px, 0) scale(0.98); }
            60%  { transform: translate3d(${(-ax * 0.25).toFixed(1)}px, ${( ay * 0.75).toFixed(1)}px, 0) scale(1.02); }
            80%  { transform: translate3d(${( ax * 0.65).toFixed(1)}px, ${( ay * 0.25).toFixed(1)}px, 0) scale(1.00); }
            100% { transform: translate3d(0, 0, 0) scale(1); }
          }
        `;
      })
      .join("\n");
  }, [blobs, parallax]);

  // animierter Hintergrundverlauf (sanft)
  const gradientCss = useMemo(() => {
    if (!colors) {
      return "";
    }
    const stops = colors.slice(0, 4).join(", ");
    const dur = Math.round(40 * speedFactor);
    return `
      .animated-blobs__bg {
        position: absolute; inset: 0;
        background: linear-gradient(120deg, ${stops});
        background-size: 200% 200%;
        animation: bgShift ${dur}s ease-in-out infinite alternate;
        filter: saturate(1.06) contrast(1.02);
      }
      @keyframes bgShift {
        0%   { background-position: 0% 50%; }
        50%  { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
    `;
  }, [colors, speedFactor]);

  // Container-Styles
  const container = useMemo(() => {
    const base = "absolute inset-0 overflow-hidden pointer-events-none";
    // GEÄNDERT: Greift auf die typsichere Map zu
    return `${base} ${VARIANT_MAP[variant]} ${className}`;
  }, [variant, className]);


  // ... (Der Rest der JSX-Rückgabe bleibt gleich) ...
  return (
    <div className={container} aria-hidden="true">
      <div className="animated-blobs__bg" />

      <svg
        className="absolute inset-0 w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
      >
        <defs>
          {goo && (
            <filter id="goo">
              <feGaussianBlur in="SourceGraphic" stdDeviation="12" result="blur" />
              <feColorMatrix
                in="blur"
                mode="matrix"
                values="
                  1 0 0 0 0
                  0 1 0 0 0
                  0 0 1 0 0
                  0 0 0 22 -10"
                result="goo"
              />
              <feBlend in="SourceGraphic" in2="goo" />
            </filter>
          )}
        </defs>

        <g
          style={{
            mixBlendMode: blendMode as any,
            filter: goo ? "url(#goo)" : undefined,
          }}
        >
          {blobs && colors && blobs.map((b, i) => (
            <g key={b.id}>
              <defs>
                <radialGradient id={`blob-grad-${i}`} cx="50%" cy="45%" r="65%">
                  <stop offset="0%" stopColor={colors[i % colors.length]} stopOpacity={0.95} />
                  <stop offset="60%" stopColor={colors[(i + 1) % colors.length]} stopOpacity={0.8} />
                  <stop offset="100%" stopColor={colors[(i + 2) % colors.length]} stopOpacity={0.55} />
                </radialGradient>
              </defs>

              <circle
                cx={`${b.cx}%`}
                cy={`${b.cy}%`}
                r={b.r}
                fill={`url(#blob-grad-${i})`}
                opacity={0.6}
                className={animated ? "animated-blob" : ""}
                style={
                  animated
                    ? {
                        animation: `${b.kf} ${b.duration}s ${b.delay}s infinite linear`,
                        transformOrigin: "center",
                      }
                    : {}
                }
              />
            </g>
          ))}
        </g>
      </svg>

      <style jsx>{`
        .animated-blob {
          will-change: transform;
          transform: translateZ(0);
          backface-visibility: hidden;
        }
        * { animation-timing-function: cubic-bezier(0.4, 0.2, 1); }
      `}</style>
      <style jsx>{keyframesCss}</style>
      <style jsx>{gradientCss}</style>
    </div>
  );
};

export default AnimatedBlobs;